import _ from 'lodash';
import { getCompanyType } from '../../../api/lendingAPI/CustomerStateAPIHelper';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import {
    GetStateResponse,
    PostDocumentUploadResponse
} from '@iwoca/lapi-client/edge';

import { UPDATE_DOCUMENT_STATUS_ON_ERROR } from './UploadDocumentsModal.actions';

export const BANK_STATMENT_DOC_TYPE = {
    label: 'bankStatement',
    value: 'bank statement'
};
const FILED_ACCOUNTS_DOC_TYPE = { label: 'filedAccounts', value: 'invoices' };
export const BUSINESS_ACCOUNTS_DOC_TYPE = {
    label: 'businessAccounts',
    value: 'business accounts'
};
const OUTSTANDING_DEBT_INFORMATION_DOC_TYPE = {
    label: 'outstandingDebtInformation',
    value: 'outstanding debt information'
};
const CASHFLOW_FORECAST_DOC_TYPE = {
    label: 'cashflowForecast',
    value: 'cashflow forecast'
};
export const VAT_RETURNS_DOC_TYPE = { label: 'vatReturn', value: 'vat return' };
const PERSONAL_ID_DOC_TYPE = {
    label: 'personalID',
    value: 'personal id'
};
export const MANAGEMENT_ACCOUNTS_DOC_TYPE = {
    label: 'managementAccounts',
    value: 'management accounts'
};
export const YEAR_END_MANAGEMENT_ACCOUNTS_DOC_TYPE = {
    label: 'yearEndManagementAccounts',
    value: 'year end management accounts'
};
export const AGED_DEBTOR_REPORT_DOC_TYPE = {
    label: 'agedDebtorReport',
    value: 'aged debtor report'
};
export const SELF_ASSESSMENT_TAX_RETURN_DOC_TYPE = {
    label: 'selfAssessmentTaxReturn',
    value: 'self assessment tax return'
};

export const MANAGEMENT_AND_BALANCE_ACCOUNTS_DOC_TYPE = {
    label: 'managementAndBalanceAccounts',
    value: 'management and balance accounts'
};

const OTHER_DOC_TYPE = { label: 'other', value: 'other' };

export type TDocumentTypes = {
    label: string;
    value: string;
    hideFromSelection?: boolean;
}[];

const ALL_DOCUMENT_TYPES = [
    AGED_DEBTOR_REPORT_DOC_TYPE,
    VAT_RETURNS_DOC_TYPE,
    BANK_STATMENT_DOC_TYPE,
    { label: 'businessPlan', value: 'business plan' },
    FILED_ACCOUNTS_DOC_TYPE,
    MANAGEMENT_ACCOUNTS_DOC_TYPE,
    { ...BUSINESS_ACCOUNTS_DOC_TYPE },
    { ...OTHER_DOC_TYPE, hideFromSelection: true },
    { ...PERSONAL_ID_DOC_TYPE, hideFromSelection: true },
    { ...CASHFLOW_FORECAST_DOC_TYPE, hideFromSelection: true },
    { ...OUTSTANDING_DEBT_INFORMATION_DOC_TYPE, hideFromSelection: true },
    {
        label: 'companyBylaws',
        value: 'company bylaws',
        hideFromSelection: true
    },
    {
        label: 'businessPlan',
        value: 'business plan',
        hideFromSelection: true
    },
    {
        label: 'tradeCertificate',
        value: 'trade certificate',
        hideFromSelection: true
    },
    {
        label: 'articlesOfAssociation',
        value: 'articles of association',
        hideFromSelection: true
    },
    {
        ...SELF_ASSESSMENT_TAX_RETURN_DOC_TYPE,
        hideFromSelection: true
    },
    {
        label: 'personalBankStatement',
        value: 'personal bank statement',
        hideFromSelection: true
    },
    {
        label: 'limitedPartnerID',
        value: 'limited partner id',
        hideFromSelection: true
    },
    {
        label: 'PNLBalance',
        value: 'pnl and balance',
        hideFromSelection: true
    },
    {
        label: 'profitAndLoss',
        value: 'profit and loss',
        hideFromSelection: true
    },
    {
        label: 'customerBrokerConsent',
        value: 'customer broker consent',
        hideFromSelection: true
    },
    {
        label: 'certificados',
        value: 'certificados',
        hideFromSelection: true
    },
    {
        label: 'balanceSheet',
        value: 'balance sheet',
        hideFromSelection: true
    }
];

export function getDocumentTypes(customer_state?: GetStateResponse) {
    switch (getCountry()) {
        case 'DE': {
            const documentTypes = [
                BANK_STATMENT_DOC_TYPE,
                MANAGEMENT_ACCOUNTS_DOC_TYPE,
                {
                    ...YEAR_END_MANAGEMENT_ACCOUNTS_DOC_TYPE,
                    hideFromSelection: true
                },
                PERSONAL_ID_DOC_TYPE,
                OTHER_DOC_TYPE,
                {
                    ...MANAGEMENT_AND_BALANCE_ACCOUNTS_DOC_TYPE,
                    hideFromSelection: true
                },
                { ...BUSINESS_ACCOUNTS_DOC_TYPE, hideFromSelection: true },
                { ...FILED_ACCOUNTS_DOC_TYPE, hideFromSelection: true }
            ];

            const listOfShareholdersDocType = {
                label: 'listOfShareholders',
                value: 'list of shareholders',
                hideFromSelection: true
            };

            const shareholderIdDocType = {
                label: 'shareholderID',
                value: 'shareholder id',
                hideFromSelection: true
            };
            const companyType = getCompanyType(customer_state!);
            if (
                _.includes(
                    [
                        'gmbh',
                        'gmbh_u_co_kg',
                        'eg',
                        'ug',
                        'ohg',
                        'ltd',
                        'aktiengesellschaft',
                        'kg'
                    ],
                    companyType
                )
            ) {
                shareholderIdDocType.hideFromSelection = false;
                listOfShareholdersDocType.hideFromSelection = false;
            }

            const shareRegisterDocType = {
                label: 'shareholderRegistry',
                value: 'share registry',
                hideFromSelection: true
            };
            if (_.includes(['kgaa', 'aktiengesellschaft'], companyType)) {
                shareRegisterDocType.hideFromSelection = false;
            }

            const tradeCertificateDocType = {
                label: 'tradeCertificate',
                value: 'trade certificate',
                hideFromSelection: true
            };
            if (_.includes(['gbr', 'gewerbebetrieb'], companyType)) {
                tradeCertificateDocType.hideFromSelection = false;
            }

            return [
                ...documentTypes,
                shareholderIdDocType,
                shareRegisterDocType,
                listOfShareholdersDocType,
                tradeCertificateDocType
            ];
        }
        case 'UK':
        default:
            return ALL_DOCUMENT_TYPES;
    }
}

type TPendingDocuments = {
    name?: string;
    pending?: boolean;
    success?: boolean;
    error?: boolean;
    code?: string;
}[];

export function updateDocumentStatusOnCompleted(
    pendingDocuments: TPendingDocuments,
    action: { successResponse: PostDocumentUploadResponse }
) {
    const { data: resolvedDocument } = action.successResponse;

    return _.map(pendingDocuments, (pendingDocument) => {
        if (pendingDocument.name === resolvedDocument.document_name) {
            pendingDocument.pending = false;
            pendingDocument.success = true;
        }

        return pendingDocument;
    });
}

export function updateDocumentStatusOnError(
    pendingDocuments: TPendingDocuments,
    action: {
        type: typeof UPDATE_DOCUMENT_STATUS_ON_ERROR;
        deserialisedErrorResponse: {
            errorCode: string;
            document: File;
            resolvedDocument: { name: string };
        };
    }
) {
    const { errorCode, document: resolvedDocument } =
        action.deserialisedErrorResponse;

    return _.map(pendingDocuments, (pendingDocument) => {
        if (pendingDocument.name === resolvedDocument.name) {
            pendingDocument.pending = false;
            pendingDocument.error = true;
            pendingDocument.code = errorCode;
        }

        return pendingDocument;
    });
}

export function serialiseDocument(document: File, documentType: string) {
    const formData = new FormData();

    formData.append('document', document);
    formData.append('document_type', documentType);

    return formData;
}
